import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Sparkles } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import api from '../lib/api';
import useWin98Scope from '../hooks/useWin98Scope';

const RegisterPage = () => {
  const { register, googleLogin } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaSiteKey, setCaptchaSiteKey] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  useWin98Scope();

  const checkCaptcha = useCallback(async () => {
    try {
      const response = await api.get('/settings/captcha-required');
      setCaptchaRequired(response.data.required);
      setCaptchaSiteKey(response.data.site_key);
    } catch (error) {
      console.error('Failed to check captcha settings');
    }
  }, []);

  useEffect(() => {
    checkCaptcha();
  }, [checkCaptcha]);

  useEffect(() => {
    if (captchaRequired && captchaSiteKey) {
      // Load reCAPTCHA script
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${captchaSiteKey}`;
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [captchaRequired, captchaSiteKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      let token = captchaToken;

      if (captchaRequired && captchaSiteKey && window.grecaptcha) {
        token = await window.grecaptcha.execute(captchaSiteKey, { action: 'register' });
      }

      await register(email, password, displayName, token);
      toast.success('Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Brand mark on the desktop */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3">
            <div className="w-12 h-12 bg-[var(--win-titlebar-start)] flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">KyberCosplay</h1>
          </div>
        </div>

        {/* Register window */}
        <div className="win-window">
          <div className="win-titlebar">
            <span>Create Account</span>
          </div>
          <div className="p-6">
            <p className="text-center text-black mb-4">Start your cosplay journey</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="displayName" className="text-black">Display Name</Label>
                <Input
                  id="displayName"
                  type="text"
                  data-testid="register-name-input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your cosplay name"
                  required
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-black">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="register-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-black">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="register-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="h-12 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-black"
                    style={{ border: 'none', boxShadow: 'none', background: 'transparent', padding: 0 }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-black">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="register-confirm-password-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-12"
                />
              </div>

              <Button
                type="submit"
                data-testid="register-submit-btn"
                disabled={loading}
                className="w-full h-12 win-btn-accent win-btn-default font-semibold"
              >
                {loading ? 'Creating…' : 'Create Account'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full win-divider" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-[var(--win-face)] text-black">or sign up with</span>
              </div>
            </div>

            <div className="flex justify-center mb-2">
              <GoogleLogin
                onSuccess={async (creds) => {
                  try {
                    await googleLogin(creds.credential);
                    toast.success('Account created!');
                  } catch {
                    toast.error('Google sign-up failed');
                  }
                }}
                onError={() => toast.error('Google sign-up failed')}
                theme="filled_black"
                shape="rectangular"
                width="320"
              />
            </div>

            <div className="mt-6 text-center">
              <p className="text-black">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-[var(--win-titlebar-start)] underline font-medium"
                  data-testid="login-link"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
